import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowTopRightOnSquareIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, ContentLoader, EmptyState } from '@/components/common';
import { api, getErrorMessage } from '@/services';
import { useAuthStore } from '@/store';
import { titleCase } from '@/utils';
import { resolveCustomerMicrosite } from './micrositeTarget';
import type { PolicyBrokerRef, PublicBrokerRow } from './micrositeTarget';

/**
 * CUSTOMER-1: the dashboard's policy-type cards linked to /quotes/new, a
 * broker-only route, so picking any type ended in "Access Denied". A customer
 * browses what their broker actually sells and asks for a quote; the broker
 * prices it. There is no self-serve purchase (2026-07-25 design decision).
 *
 * 2026-07-30: picking a type now hands the customer off to their broker's
 * PUBLIC microsite instead of this in-app catalog, when the broker has one.
 * Two things are deliberate:
 *
 * * We land on the microsite HOME (`/policy/site/<slug>/`), not on a
 *   `company/<insurer_slug>/<policy_slug>/` deep link. A product slug is
 *   `BrokerPolicyAccess.override_seo_slug` -> `InsurancePolicy.seo_slug` ->
 *   `slugify("<name>-<id[:8]}")` (apps/microsite/views_public._policy_slug) and
 *   an insurer page 404s outright when that insurer has no product the RESOLVED
 *   broker can sell. None of that is knowable from here, and a wrong guess is a
 *   hard 404 -- so we hand over at the storefront, where every product link is
 *   built server-side and correct.
 * * A broker with `is_public_profile_enabled` off has no site to send anyone
 *   to, so this page keeps rendering the catalog exactly as before.
 */

interface CatalogProduct {
  id: string;
  name: string;
  description: string;
  insurance_type: string | null;
}

interface CatalogInsurer {
  insurer_id: string;
  insurer_name: string;
  insurer_logo: string | null;
  claim_settlement_ratio: number | null;
  products: CatalogProduct[];
}

interface CatalogResponse {
  insurance_type: string;
  insurers: CatalogInsurer[];
}

interface PaginatedResponse<T> {
  results?: T[];
  next?: string | null;
}

/**
 * The public directory is paginated (`max_page_size = 100`), and it is looked
 * up by `public_profile_slug` -- there is no by-id retrieve -- so membership
 * has to be tested against the whole list. Capped at 10 pages so a platform
 * with a runaway broker count degrades to "no microsite" (the in-app catalog)
 * rather than to a stalled page.
 */
async function fetchPublicBrokers(): Promise<PublicBrokerRow[]> {
  const rows: PublicBrokerRow[] = [];
  let url: string | null = '/policy/api/public/brokers/?page_size=100';
  for (let page = 0; url && page < 10; page += 1) {
    // Annotated (not inferred): `url` is reassigned from `body.next`, and
    // inferring `body` from the awaited call makes that a circular reference.
    const body: PaginatedResponse<PublicBrokerRow> | PublicBrokerRow[] = (await axios.get(url)).data;
    if (Array.isArray(body)) {
      rows.push(...body);
      url = null;
    } else {
      rows.push(...(body?.results ?? []));
      url = body?.next ?? null;
    }
  }
  return rows;
}

export function BuyPolicyPage() {
  const { type = 'motor' } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [requestedFor, setRequestedFor] = useState<string | null>(null);

  // Shares the 'customer-me' key with the dashboard and My Contacts, so this is
  // usually a cache hit rather than a third request.
  const profileQuery = useQuery<{ broker?: string | null; broker_name?: string | null }>({
    queryKey: ['customer-me'],
    queryFn: async () => (await api.get('/customers/me/')).data,
    retry: false,
  });

  // Every broker this login actually holds a policy with. PolicyViewSet scopes a
  // customer by `customer__user`, so this spans all their linked Customer rows.
  const policyBrokersQuery = useQuery<PolicyBrokerRef[]>({
    queryKey: ['customer-policy-brokers'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<PolicyBrokerRef>>('/policies/?page_size=100');
      return res.data?.results ?? [];
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const publicBrokersQuery = useQuery<PublicBrokerRow[]>({
    queryKey: ['public-broker-directory'],
    queryFn: fetchPublicBrokers,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  // Every input has either settled or failed. A failure is not fatal: it just
  // means less evidence, and at worst no microsite -> the catalog below.
  const isResolving =
    profileQuery.isLoading || policyBrokersQuery.isLoading || publicBrokersQuery.isLoading;

  const micrositeTarget = useMemo(
    () =>
      isResolving
        ? null
        : resolveCustomerMicrosite({
            policies: policyBrokersQuery.data ?? [],
            profileBrokerId: profileQuery.data?.broker ?? null,
            profileBrokerName: profileQuery.data?.broker_name ?? null,
            publicBrokers: publicBrokersQuery.data ?? [],
          }),
    [isResolving, policyBrokersQuery.data, profileQuery.data, publicBrokersQuery.data]
  );

  // `replace`, not `assign`: with `assign` the browser Back button from the
  // microsite would land back here and immediately bounce forward again,
  // trapping the customer. Replacing sends Back to wherever they came from.
  useEffect(() => {
    if (micrositeTarget) window.location.replace(micrositeTarget.url);
  }, [micrositeTarget]);

  // The in-app catalog is only the fallback now, so it is not fetched until we
  // know there is no microsite to hand off to.
  const { data, isLoading } = useQuery<CatalogResponse>({
    queryKey: ['customer-catalog', type],
    queryFn: async () => (await api.get(`/products/catalog/?type=${encodeURIComponent(type)}`)).data,
    enabled: !isResolving && !micrositeTarget,
  });

  // The quote request lands in the broker's Quotes list as a draft. Pricing,
  // insurer selection and issuance all stay on the broker side.
  const requestQuote = useMutation({
    mutationFn: async (insurer: CatalogInsurer) => {
      return api.post('/quotes/requests/', {
        insurance_type: type,
        business_type: 'new',
        proposer_type: 'individual',
        proposer_name: user?.full_name ?? '',
        proposer_email: user?.email ?? '',
        proposer_phone: user?.phone ?? '',
        notes: `Customer-requested quote for ${insurer.insurer_name} (${titleCase(type)}).`,
      });
    },
    onSuccess: (_res, insurer) => {
      setRequestedFor(insurer.insurer_id);
      toast.success('Quote request sent to your broker.');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const insurers = data?.insurers ?? [];

  if (isResolving) {
    return (
      <div>
        <PageHeader
          title={`${titleCase(type)} Insurance`}
          subtitle="Finding the plans available to you"
          breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: titleCase(type) }]}
        />
        <ContentLoader />
      </div>
    );
  }

  // Hand-off. The redirect above has already fired; this is what the customer
  // sees for the instant before the browser follows it, and the manual link is
  // the escape hatch if it is blocked.
  if (micrositeTarget) {
    return (
      <div>
        <PageHeader
          title={`${titleCase(type)} Insurance`}
          subtitle={`Taking you to ${micrositeTarget.brokerName}`}
          breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: titleCase(type) }]}
        />
        <Card>
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <ContentLoader />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Opening {micrositeTarget.brokerName}&apos;s website, where you can browse{' '}
              {titleCase(type)} plans and request a premium.
            </p>
            <a
              href={micrositeTarget.url}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 dark:hover:text-primary-300 dark:text-primary-400"
            >
              Continue
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`${titleCase(type)} Insurance`}
        subtitle="Insurers and plans available through your broker"
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: titleCase(type) }]}
        actions={
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        }
      />

      {isLoading ? (
        <ContentLoader />
      ) : insurers.length === 0 ? (
        <Card>
          <EmptyState
            title="No plans available yet"
            description="Your broker has not enabled any insurer for this policy type. Contact your advisor and they can raise a quote for you."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {insurers.map((insurer) => (
            <Card key={insurer.insurer_id}>
              <CardHeader
                title={insurer.insurer_name}
                subtitle={
                  insurer.claim_settlement_ratio
                    ? `Claim settlement ratio: ${insurer.claim_settlement_ratio}%`
                    : undefined
                }
                action={
                  <Button
                    size="sm"
                    isLoading={requestQuote.isPending}
                    disabled={requestedFor === insurer.insurer_id}
                    onClick={() => requestQuote.mutate(insurer)}
                  >
                    {requestedFor === insurer.insurer_id ? 'Requested' : 'Request a Quote'}
                  </Button>
                }
              />
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {insurer.products.map((product) => (
                  <li key={product.id} className="py-3 flex items-start gap-3">
                    <BuildingOffice2Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</p>
                      {product.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{product.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
